import { csvEmailRegex } from '../email-regex/email-regex';

const capitalizeFirstLetter = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1);

export class TenantValidator {
  maxNameLength = 80;
  minNameLength = 2;
  maxDescriptionLength = 300;
  minDescriptionLength = 0;
  maxSlugsLength = 300;
  minSlugsLength = 0;
  maxEmailsLength = 4000;
  minEmailsLength = 0;
  slugsRegex = /^(?! )([a-zA-Z0-9-]*,[ ])*[a-zA-Z0-9-]*(?!< )$/;

  validationObj = {
    error: {
      name: '',
      description: '',
      slugs: '',
      emails: '',
      monthlyPrice: '',
    },
  };

  clearError(fieldName: string) {
    this.validationObj.error[fieldName] = '';
  }

  stringValidation(
    s: string,
    fieldName: string,
    min: number,
    max: number,
    required = false,
    allowSpecialChars = false
  ) {
    const err = this.validationObj.error;

    const allowedCharacters = allowSpecialChars
      ? /^[a-zA-Z0-9 @&.,'?!()"~`;_+:{}$%^*#\-/\n]*$/
      : /^[a-zA-Z0-9 &\-\n]*$/;

    const startsWithDisallowed = /^[^a-zA-Z0-9]/;
    const endsWithNewline = /\n$/;
    const endsWithDisallowed = /[^a-zA-Z0-9.\n!]$/;

    if (!s && required) {
      err[fieldName] = `${fieldName} is required`;
    } else if (!allowedCharacters.test(s)) {
      err[fieldName] = `${fieldName} must only contain allowed characters`;
    } else if (endsWithNewline.test(s)) {
      err[fieldName] = `${fieldName} must not end with a new line`;
    } else if (startsWithDisallowed.test(s) || endsWithDisallowed.test(s)) {
      err[
        fieldName
      ] = `${fieldName} must not start or end with a special character`;
    } else if (s.length < min) {
      err[fieldName] = `${fieldName} must be at least ${min} characters long`;
    } else if (s.length > max) {
      err[fieldName] = `${fieldName} must be at most ${max} characters long`;
    }

    if (err[fieldName]) {
      err[fieldName] = capitalizeFirstLetter(err[fieldName]);
    }
  }

  validateName(s: string) {
    this.clearError('name');
    this.stringValidation(
      s,
      'name',
      this.minNameLength,
      this.maxNameLength,
      true
    );

    return this;
  }

  validateDescription(s: string) {
    this.clearError('description');
    this.stringValidation(
      s,
      'description',
      this.minDescriptionLength,
      this.maxDescriptionLength,
      true,
      true
    );

    return this;
  }

  validateSlugs(s: string) {
    const slugList = s.split(',');

    const err = this.validationObj.error;
    this.clearError('slugs');

    if (s.length === 0) {
      err.slugs = 'Slugs are required';
    }

    const errorMessage =
      'Slugs must only contain letters and numbers, and be a valid CSV if using a comma';

    for (let slug of slugList) {
      slug = slug.trim();

      if (slug.match(this.slugsRegex) === null) {
        err.slugs = errorMessage;
        break;
      }
    }

    return this;
  }

  validateEmails(s: string, required = false) {
    this.clearError('emails');
    const wildcard = '.*';

    if (required && s.length === 0) {
      this.validationObj.error.emails = 'Emails are required';
    }

    const emailList = s.split(',');

    for (let email of emailList) {
      email = email.trim();

      if (email !== wildcard && !csvEmailRegex.test(email)) {
        this.validationObj.error.emails =
          'Emails must be a valid CSV of emails or a single email format';
        break;
      }
    }

    return this;
  }

  validateMonthlyPrice(s: number) {
    this.clearError('monthlyPrice');
    const err = this.validationObj.error;

    if (!s) {
      err.monthlyPrice = 'Monthly price is required';
    }

    return this;
  }
}
